import type { FormContext } from "vee-validate"
import type { Ref } from "vue"
import { fromZonedTime, toZonedTime } from "date-fns-tz"
import { computed, watch } from "vue"
import { useGetTimezoneFromLocation } from "@/api"
import { Location } from "@/models"

interface EventDatetimeFormValues {
  eventDate?: Date
  eventEndDate?: Date
  timezone?: string
  location?: Partial<Location>
}

export function useEventDatetimeForm(
  form: Pick<FormContext<EventDatetimeFormValues>, "values" | "setFieldValue">,
  enableEventTime: Ref<boolean>,
) {
  /**
   * Get timezone from form location
   * This is used to get the timezone from the location selected in the form
   * and update the timezone field in the form
   *
   * Note: We use computed instead of toRef to avoid reactive references that could
   * include non-clonable objects (like File objects) in the query key
   */
  const location = computed<Location | null>(() => form.values.location ? new Location(form.values.location) : null)

  const { data: locationTimezone, isLoading: isLoadingLocationTimezone } = useGetTimezoneFromLocation(location)

  // Watch for timezone changes by location and update the form
  watch(locationTimezone, (tz) => {
    form.setFieldValue("timezone", tz?.timeZoneId || undefined)
  })

  /**
   * Computed property that converts the selected event date to UTC for submission
   * Returns the original date if time selection is disabled or timezone/date is missing
   * Otherwise converts the date from the selected timezone to UTC
   */
  const eventDateForSubmission = computed(() => {
    if (!enableEventTime.value || !form.values.eventDate || !form.values.timezone)
      return form.values.eventDate

    return fromZonedTime(form.values.eventDate, form.values.timezone)
  })

  const eventEndDateForSubmission = computed(() => {
    if (!enableEventTime.value || !form.values.eventEndDate || !form.values.timezone)
      return form.values.eventEndDate

    return fromZonedTime(form.values.eventEndDate, form.values.timezone)
  })

  /**
   * Initializes the event date and timezone in the form
   * If a timezone is provided, converts the UTC date to that timezone for display
   */
  const initializeEventDate = (eventDate: Date | undefined, eventTimezone?: string) => {
    if (!eventDate)
      return

    if (eventTimezone) {
      form.setFieldValue("eventDate", toZonedTime(eventDate, eventTimezone))
      form.setFieldValue("timezone", eventTimezone)
      enableEventTime.value = true
    }
    else {
      form.setFieldValue("eventDate", eventDate)
    }
  }

  const initializeEventEndDate = (eventEndDate: Date | undefined, eventTimezone?: string) => {
    if (!eventEndDate)
      return

    if (eventTimezone) {
      form.setFieldValue("eventEndDate", toZonedTime(eventEndDate, eventTimezone))
      return
    }

    form.setFieldValue("eventEndDate", eventEndDate)
  }

  return {
    eventDateForSubmission,
    eventEndDateForSubmission,
    isLoadingLocationTimezone,
    initializeEventDate,
    initializeEventEndDate,
  }
}
